import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, FileSignature, MessageCircle, Pencil, Trash2 } from 'lucide-react';
import { api, formatDate, fullName, mensajeError } from '../../lib/api';
import { EmptyState } from '../Common';
import { Button } from '../ui/button';
import { InformeDialog } from '../../pages/InformesPage';
import EnviarInformeWhatsAppDialog from './EnviarInformeWhatsAppDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** «el 24/09/2026 · inglés · <quién lo mandó>» del último envío por WhatsApp. */
function ultimoEnvio(inf) {
  const envios = inf?.envios_whatsapp || [];
  if (!envios.length) return null;
  const e = envios[envios.length - 1];
  const idioma = e.lang === 'en' ? 'en inglés' : 'en español';
  return `Enviado por WhatsApp ${idioma} el ${formatDate(e.at)}${e.por_nombre ? ` por ${e.por_nombre}` : ''}`
    + (envios.length > 1 ? ` (${envios.length} envíos)` : '');
}

/**
 * Los informes médicos de la ficha (24/9/2026): verlos, corregirlos,
 * borrarlos y mandarlos por WhatsApp en español o en inglés.
 *
 *  · Editar y borrar: cualquier persona del personal. El servidor guarda la
 *    versión anterior en el historial del informe, y borrar es recuperable.
 *    Lo que el servidor no deje hacer se dice en la pantalla, nunca en
 *    silencio.
 *  · WhatsApp: los mismos que hoy mandan WhatsApp a los pacientes (gestión);
 *    al resto no se le enseña un botón que le contestaría «no».
 */
export default function InformesPacienteTab({ patient, informes, onPreview, onReload, puedeWhatsApp }) {
  const [editando, setEditando] = useState(null);
  const [enviando, setEnviando] = useState(null);
  const [borrando, setBorrando] = useState(null);
  const [error, setError] = useState('');

  const nombre = fullName(patient) || 'este paciente';

  const borrar = async (inf) => {
    const aviso = `¿Borrar el informe «${inf.title || 'sin título'}» de ${nombre}`
      + `${inf.doc_date ? ` (${formatDate(inf.doc_date)})` : ''}?\n\n`
      + 'Desaparece de la ficha y de las listas. Queda registrado quién lo borra y se puede recuperar.';
    if (!window.confirm(aviso)) return;
    setBorrando(inf.id);
    setError('');
    try {
      await api.delete(`/api/informes/${inf.id}`);
      toast.success('Informe borrado');
      onReload?.();
    } catch (e) {
      const texto = mensajeError(e, 'No se ha podido borrar el informe.');
      setError(`No se ha borrado «${inf.title || 'el informe'}»: ${texto}`);
      toast.error(texto);
    } finally {
      setBorrando(null);
    }
  };

  if (!informes.length) {
    return (
      <EmptyState icon={FileSignature} title="Sin informes" description="Redacta un informe desde la sección Informes." />
    );
  }

  return (
    <>
      {error && (
        <p
          role="alert"
          className="mb-2 text-sm rounded-md bg-red-50 text-red-900 border border-red-200 px-3 py-2"
          data-testid="patient-informes-error"
        >
          {error}
        </p>
      )}
      <div className="divide-y divide-border" data-testid="patient-informes-list">
        {informes.map((inf) => {
          const envio = ultimoEnvio(inf);
          return (
            <div key={inf.id} className="py-2 flex items-center gap-2" data-testid={`patient-informe-${inf.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{inf.title}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(inf.doc_date)} · {inf.created_by_name || ''}
                  {inf.ediciones > 0 && (
                    <span data-testid={`patient-informe-editado-${inf.id}`}>
                      {' · '}editado{inf.updated_by_name ? ` por ${inf.updated_by_name}` : ''}
                    </span>
                  )}
                </div>
                {envio && (
                  <div className="text-xs text-[#2E6E5E]" data-testid={`patient-informe-enviado-${inf.id}`}>
                    {envio}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                title="Ver el PDF"
                onClick={() => onPreview(`${BACKEND_URL}/api/informes/${inf.id}/pdf`, `informe_${patient?.nif || patient?.id}.pdf`, `Informe · ${inf.title}`)}
                data-testid={`patient-informe-open-${inf.id}`}
              >
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                title="Editar el informe"
                onClick={() => { setError(''); setEditando(inf); }}
                data-testid={`patient-informe-edit-${inf.id}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              {puedeWhatsApp && (
                <Button
                  size="sm"
                  variant="outline"
                  title="Enviar por WhatsApp"
                  onClick={() => { setError(''); setEnviando(inf); }}
                  data-testid={`patient-informe-whatsapp-${inf.id}`}
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" />
                  <span className="sr-only">Enviar por WhatsApp</span>
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                title="Borrar el informe"
                className="text-destructive"
                disabled={borrando === inf.id}
                onClick={() => borrar(inf)}
                data-testid={`patient-informe-delete-${inf.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
      </div>

      <InformeDialog
        open={!!editando}
        onOpenChange={(v) => { if (!v) setEditando(null); }}
        initial={editando}
        patients={patient ? [patient] : []}
        onSaved={() => onReload?.()}
      />

      <EnviarInformeWhatsAppDialog
        informe={enviando}
        open={!!enviando}
        onOpenChange={(v) => { if (!v) setEnviando(null); }}
        onSent={() => onReload?.()}
      />
    </>
  );
}
